import { useMemo, useState } from 'react';
import { IExpressionVariableConstants } from '@shared/models/expression-variables.model';
import { expressionVariablesConstants } from '@shared/constants/expression-variables.constants';
import {
  emptyStoreySummedValues,
  IStoreySum,
  IEnrichedStorey,
} from '@shared/helpers/storeys.helpers';
import { sumStoreysFromDesignExpressionVariables } from '@shared/helpers/design/expressions/storey-sum-from-design.helpers';
import { EXPRESSION_VARIABLES_CONSTANTS_ABBREVIATION } from '@shared/constants/calculations.constants';
import { getVariableWithNoConstantObjectBefore } from './calculations.helpers';
import { useDesignExpressionVariables } from '@features/design/hooks/design.hooks';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';

type GetVariableSumFunc = (
  hiddenVariableSums: IEnrichedStorey,
  storeySumExpressionVariables: IStoreySum,
  variable?: string,
) => string;

const getVariableSum: GetVariableSumFunc = (
  hiddenVariableSums,
  storeySumExpressionVariables,
  variable,
) => {
  if (variable?.startsWith(EXPRESSION_VARIABLES_CONSTANTS_ABBREVIATION)) {
    return getDisplayNumber(
      expressionVariablesConstants[
        getVariableWithNoConstantObjectBefore(
          variable,
        ) as keyof IExpressionVariableConstants
      ] as number,
    );
  }
  if (
    variable &&
    hiddenVariableSums[variable as keyof IEnrichedStorey] !== undefined
  ) {
    return getDisplayNumber(
      hiddenVariableSums[variable as keyof IEnrichedStorey],
    );
  }
  return getDisplayNumber(
    storeySumExpressionVariables[variable as keyof IStoreySum] as number,
  );
};

interface CalculationsUtils {
  storeySumExpressionVariables: {
    storeys: IEnrichedStorey[];
    sum: IStoreySum;
  };
  activeVariable: string | undefined;
  setActiveVariable: (variable?: string) => void;
  hiddenVariableSums: IEnrichedStorey;
  variableSumValue: string;
  getVariableSum: GetVariableSumFunc;
}

export const useCalculations = (): CalculationsUtils => {
  const designVariables = useDesignExpressionVariables();
  const storeySumExpressionVariables = useMemo(() => {
    if (!designVariables) {
      return { storeys: [], sum: { ...emptyStoreySummedValues } };
    }
    return sumStoreysFromDesignExpressionVariables(designVariables);
  }, [designVariables]);

  const [activeVariable, setActiveVariable] = useState<string | undefined>(
    undefined,
  );

  const hiddenVariableSums: IEnrichedStorey = useMemo(() => {
    const firstStorey = storeySumExpressionVariables.storeys[0];

    if (!firstStorey) {
      console.error('Could not find storey');
      return {} as IEnrichedStorey;
    }

    const sums: Record<keyof IEnrichedStorey, number> = Object.keys(firstStorey)
      .filter(
        (key) =>
          storeySumExpressionVariables.sum[key as keyof IStoreySum] ===
          undefined,
      )
      .reduce(
        (acc, key) => ({ ...acc, [key as keyof IEnrichedStorey]: 0 }),
        {} as IEnrichedStorey,
      );

    storeySumExpressionVariables.storeys.forEach((storey) => {
      Object.entries(storey).forEach(([key, val]) => {
        sums[key as keyof IEnrichedStorey] = sums[key as keyof IEnrichedStorey]
          ? sums[key as keyof IEnrichedStorey] + val
          : val;
      });
    });

    return sums;
  }, [storeySumExpressionVariables]);

  const variableSumValue = useMemo(
    () =>
      getVariableSum(
        hiddenVariableSums,
        storeySumExpressionVariables.sum,
        activeVariable,
      ),
    [hiddenVariableSums, storeySumExpressionVariables.sum, activeVariable],
  );

  return {
    storeySumExpressionVariables,
    activeVariable,
    setActiveVariable,
    hiddenVariableSums,
    variableSumValue,
    getVariableSum,
  };
};
